import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import {
  fetchActivityReservations,
  getActivityReservationStats,
} from "../../../../api/CRS/Activities";
import Loader from "../../../../components/Loader";
import {
  genericIndexesStyles,
  modalGenericStyles,
  tableStyles,
  variables,
} from "../../../../variables";
import Navbar from "./Navbar";
import _ from "lodash";
import ControlStrip from "../../../../components/ControlStrip";
import { handleApplyFilters, medWeekdayDatetimer } from "../../../../helpers";
import DetailActivityModal from "../ActivitiesModals/DetailActivityModal";
import ReservationStats from "../../CommonCRSModals/ReservationStats/ReservationStats";
import { commonTableHeaders } from "../../helpers";
import { useQueries } from "@tanstack/react-query";
import HamburgerMenu from "../../../../components/HamburgerMenu";
import { genericRequestErrorHandler } from "../../../../api";
import { useListPageSidebar } from "../../../../hooks";
import * as yup from "yup";
import { DateTime } from "luxon";
import { CustomButton } from "../../../../components/CustomButton";
import CRSCxlCell from "../../../../components/CRSCxlCell";

const initialFilters = {
  limit: 20,
  page: 1,
  ordering: "",
  discending: "",
  name__icontains: "",
  start_date__gte: "",
  end_date__lte: "",
  market_type: "",
  created__gte: "",
  created__lte: "",
  external_reference__icontains: "",
  source_entity: "",
  status: "",
  network_search: "",
  destination_name__icontains: "",
  supplier_reservation_reference: "",
  reference: "",
};
const navbarSchema = yup.object().shape({
  reference: yup.string(),
  created__gte: yup.date(),
  created__lte: yup.date().max(new Date(), "Date cannot be in the future"),
});

const activitieStyles = createUseStyles({
  Activities: {
    ...genericIndexesStyles.indexes.mobile(),
    gridTemplateRows: "auto auto auto  1fr",
  },
  card: { ...modalGenericStyles.card },
  tableContainer: genericIndexesStyles.tableContainer.mobile(),
  table: tableStyles.table,
  headRow: tableStyles.head.row,
  headCell: tableStyles.head.cell,
  bodyRow: tableStyles.body.row,
  tablecell: tableStyles.body.cell,
  actionCell: {
    ...tableStyles.body.cell,
    gap: variables.half_gap,
  },
  datesCell: { ...tableStyles.body.cell, minWidth: "9rem", width: "9rem" },
  [`@media ${variables.media.smallscreen}`]: {
    tableCell: {
      padding: `calc(${variables.normal_gap} * 2.5) 0`,
    },
  },
});
const Activities = () => {
  const classes = activitieStyles();

  useListPageSidebar();

  const [filters, setFilters] = useState(initialFilters);
  const [reference, setReference] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const tableHeaders = commonTableHeaders({
    "Supplier Reservation Reference": (val) =>
      val.supplier_reservation_reference,
    Destination: (val) => val.destination_name,
    Name: (val) => val.name,
    Tags: (val) => _.get(val, "tags", []).join(","),
    Adults: (val) => val.adults,
    Children: (val) => val.children || "0",
    "Start Date": (val) => val.start_date,
    "End Date": (val) => val.end_date,
    "Market Type": (val) => val.market_type,
    Cxl: (val) => <CRSCxlCell val={val} />,
    "Supplier Reference": (val) => val.supplier_reservation_reference,
  });
  const handleApplyLocalFilters = (filters) => {
    handleApplyFilters(filters, setFilters, navbarSchema);
  };
  const results = useQueries({
    queries: [
      {
        queryKey: ["activities", filters],
        queryFn: async () => {
          let correctPayload = { ...filters };
          if (filters.created__lte) {
            correctPayload["created__lte"] = DateTime.fromISO(
              filters.created__lte
            ).toISO();
          }
          if (filters.created__gte) {
            correctPayload["created__gte"] = DateTime.fromISO(
              filters.created__gte
            ).toISO();
          }
          if (filters.start_date__gte) {
            correctPayload["start_date__gte"] = DateTime.fromISO(
              filters.start_date__gte
            ).toISO();
          }
          if (filters.end_date__lte) {
            correctPayload["end_date__lte"] = DateTime.fromISO(
              filters.end_date__lte
            ).toISO();
          }
          return fetchActivityReservations({
            params: correctPayload,
          });
        },
        enabled: !showDetailModal,
        onError: (error) => genericRequestErrorHandler(error),
      },
      {
        queryKey: ["activitiesStats", filters],
        queryFn: async () => {
          let correctPayload = { ...filters };
          if (filters.created__lte) {
            correctPayload["created__lte"] = DateTime.fromISO(
              filters.created__lte
            ).toISO();
          }
          if (filters.created__gte) {
            correctPayload["created__gte"] = DateTime.fromISO(
              filters.created__gte
            ).toISO();
          }
          if (filters.start_date__gte) {
            correctPayload["start_date__gte"] = DateTime.fromISO(
              filters.created__gte
            ).toISO();
          }
          if (filters.end_date__lte) {
            correctPayload["end_date__lte"] = DateTime.fromISO(
              filters.created__gte
            ).toISO();
          }
          return getActivityReservationStats({
            params: correctPayload,
          });
        },
        enabled: !showDetailModal,
      },
    ],
  });
  return (
    <div id="ACTListPage" className={classes.Activities}>
      <Navbar
        filters={filters}
        initialFilters={initialFilters}
        onApplyFilters={handleApplyLocalFilters}
      />
      <HamburgerMenu />
      {!_.get(results, "1.isLoading", true) &&
        _.get(results, "1.data", null) && (
          <ReservationStats
            title="Reservation Stats"
            stats={_.get(results, "1.data.data.stats_per_currency")}
          />
        )}
      <ControlStrip
        limit={_.get(filters, "limit")}
        page={filters.page}
        count={_.get(results, "0.data.data.count")}
        onChangeLimit={(limit) => setFilters({ ...filters, limit, page: 1 })}
        onChangePage={(page) => setFilters({ ...filters, page })}
        orderOptions={[["created", "Created"]]}
        onChangeOrderOptions={(ordering) =>
          setFilters({ ...filters, ordering })
        }
      />
      {_.get(results, "0.isLoading", false) ? (
        <Loader />
      ) : (
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.thead}>
              <tr className={classes.headRow}>
                <th className={classes.headCell}>#</th>
                {Object.keys(tableHeaders).map((value, idx) => (
                  <th key={idx} className={classes.headCell}>
                    {value}
                  </th>
                ))}
                <th className={classes.headCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {_.get(results, "0.data.data.results", []).map((val, idx) => (
                <tr className={classes.bodyRow} key={idx}>
                  <td className={classes.tablecell}>{idx + 1}</td>
                  {Object.entries(tableHeaders).map((entry, ridx) => {
                    var cell = null;
                    const fn = entry[1];
                    switch (entry[0].toLowerCase()) {
                      case "end date":
                        cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                        return (
                          <td className={classes.datesCell} key={ridx}>
                            {cell}
                          </td>
                        );
                      case "start date":
                        cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                        return (
                          <td className={classes.datesCell} key={ridx}>
                            {cell}
                          </td>
                        );
                      case "created":
                        cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                        return (
                          <td className={classes.datesCell} key={ridx}>
                            {cell}
                          </td>
                        );
                      default:
                        cell = fn(val) || "N/A";
                        break;
                    }
                    return (
                      <td className={classes.tablecell} key={ridx}>
                        {cell}
                      </td>
                    );
                  })}
                  <td className={classes.actionCell}>
                    <CustomButton
                      id={`detailACTButton_${idx}`}
                      appearance="ghost"
                      onClick={() => {
                        setReference(val.reference);
                        setShowDetailModal(true);
                      }}>
                      <strong>Details</strong>
                    </CustomButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showDetailModal && (
        <DetailActivityModal
          onClose={() => setShowDetailModal(false)}
          reference={reference}
        />
      )}
    </div>
  );
};

export default Activities;
